import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './CreateProcess.scss';
import {
    useGetV1DocumentTypeByProcessTypeProcessTypeId,
    usePostV1ProcessAddProcess,
    usePostV1FillPdfGenerateUploadExcelTemplate,
    usePostV1FillOnlineFormGenerateUploadExcelTemplate,
} from '../../api/generated/endpoint';
import axios from 'axios';
import {
    AddProcessRequest,
    DownloadExampleExportTemplateRequest,
    GenerateExcelTemplateRequest,
    GenerateExcelTemplateResponse,
} from '../../api/generated/models';
import useToast from '../../helpers/useToast';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import useLocale from '../../locale/useLocale';
import { useAuth } from 'react-oidc-context';
import { Panel } from '@appkit4/react-components/panel';
import { Button } from '@appkit4/react-components/button';
import { Upload } from '@appkit4/react-components/upload';
import { Badge } from '@appkit4/react-components';
import { Input } from '@appkit4/react-components/field';
import { Combobox } from '@appkit4/react-components';
import { Checkbox } from '@appkit4/react-components/checkbox';
import FolderExplorer from './FolderExplorer';
import { Accordion, AccordionItem } from '@appkit4/react-components/accordion';
import { useFileUpload } from '../../helpers/useFileUpload';
import { useTenantProcessTypes } from '../../helpers/useTenantProcessTypes';

enum ProcessType {
    ExtractFromPDF = 1,
    FillInPDF = 2,
    FillInOnlineForm = 3,
}

const processTypeOptions = [
    {
        value: ProcessType.ExtractFromPDF,
        label: 'Extract from PDF',
    },
    {
        value: ProcessType.FillInPDF,
        label: 'Fill in PDF',
    },
    {
        value: ProcessType.FillInOnlineForm,
        label: 'Fill in Online Form',
    },
];

const CreateProcess: React.FC = () => {
    const showToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();
    const auth = useAuth();
    const { enabledSet, loading: processTypesLoading } =
        useTenantProcessTypes();
    const location = useLocation();
    const {
        folderId,
        folderName,
        processType,
    }: { folderId: number; folderName: string; processType: ProcessType } =
        location.state || {};

    const navigate = useNavigate();
    const [isUploading, setIsUploading] = useState(false);
    const [formData, setFormData] = useState({
        processName: '',
        processType: processType || ProcessType.ExtractFromPDF,
        location: folderId || 0,
        folderName: folderName || '',
    });

    const docTypesExtract = useGetV1DocumentTypeByProcessTypeProcessTypeId(
        ProcessType.ExtractFromPDF
    );
    const docTypesFillPdf = useGetV1DocumentTypeByProcessTypeProcessTypeId(
        ProcessType.FillInPDF
    );
    const docTypesOnline = useGetV1DocumentTypeByProcessTypeProcessTypeId(
        ProcessType.FillInOnlineForm
    );
    const { mutate: addProcess } = usePostV1ProcessAddProcess();
    const { uploadFiles } = useFileUpload();
    const generateFillPDFTemplateMutation =
        usePostV1FillPdfGenerateUploadExcelTemplate();
    const generateExcelTemplateMutation =
        usePostV1FillOnlineFormGenerateUploadExcelTemplate();

    useEffect(() => {
        setSelectedDocumentTypeIds([]);
    }, [formData.processType]);

    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<
        number[]
    >([]);
    const [files, setFiles] = useState<any[]>([]);
    const [validateUnnecessaryFields, setValidateUnnecessaryFields] =
        useState(true);
    const [activeKeys, setActiveKeys] = React.useState<string[]>([]);
    const processNameRef: any = useRef(null);

    const areRequiredFieldsFilled = () => {
        return (
            formData.processName.trim() !== '' &&
            formData.processType !== null &&
            formData.location !== -1 &&
            selectedDocumentTypeIds.length > 0
        );
    };

    const availableProcessTypeOptions = useMemo(
        () => processTypeOptions.filter((o) => enabledSet.has(o.value)),
        [enabledSet]
    );

    useEffect(() => {
        if (!processTypesLoading && !enabledSet.has(formData.processType)) {
            const fallback = availableProcessTypeOptions[0]?.value;
            if (fallback) {
                setFormData((prev) => ({ ...prev, processType: fallback }));
            }
        }
    }, [enabledSet, processTypesLoading]);

    const documentTypes = useMemo(() => {
        const src =
            formData.processType === ProcessType.ExtractFromPDF
                ? docTypesExtract.data
                : formData.processType === ProcessType.FillInPDF
                ? docTypesFillPdf.data
                : docTypesOnline.data;

        return (src ?? []).map((dt) => ({
            value: dt.documentTypeId ?? 1,
            label: dt.documentTypeName ?? 'name',
        }));
    }, [
        formData.processType,
        docTypesExtract.data,
        docTypesFillPdf.data,
        docTypesOnline.data,
    ]);

    const isLoadingDocTypes =
        formData.processType === ProcessType.ExtractFromPDF
            ? docTypesExtract.isLoading || docTypesExtract.isFetching
            : formData.processType === ProcessType.FillInPDF
            ? docTypesFillPdf.isLoading || docTypesFillPdf.isFetching
            : docTypesOnline.isLoading || docTypesOnline.isFetching;

    const handleBlur = () => {
        if (processNameRef.current) {
            setFormData((prevData) => ({
                ...prevData,
                processName: processNameRef.current.value,
            }));
        }
    };

    const handleProcessTypeChange = (value: number | null) => {
        if (value !== null) {
            setFormData((prevData) => ({
                ...prevData,
                processType: value,
            }));
        }
    };

    const handleDocumentTypeChange = (values: number[]) => {
        setSelectedDocumentTypeIds(values);
    };

    const onClickAccordion = (activeKeys: string[]) => {
        setActiveKeys(activeKeys);
    };

    const handleFolderSelect = (
        selectedFolderId: string,
        selectedFolderName: string
    ) => {
        if (selectedFolderName !== 'No sub folders')
            setFormData((prevData) => ({
                ...prevData,
                location: Number(selectedFolderId),
                folderName: selectedFolderName,
            }));
    };

    const handleFileChange = (file: File, fileList: any): void => {
        setFiles(fileList);
    };

    const handleOnlineFormUpload = async (processId: number, file: any) => {
        try {
            const formData = new FormData();
            formData.append('ProcessId', processId.toString());
            formData.append('File', file.originFile || file);
            formData.append(
                'ValidateUnnecessaryFields',
                validateUnnecessaryFields.toString()
            );

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            const resp = await axios.post(
                '/v1/FillOnlineForm/UploadExcel',
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    baseURL: process.env.REACT_APP_API_BASE_URL || '',
                }
            );

            const data: any = resp?.data;
            if (
                (data && data.isSuccess === false) ||
                (data && data.IsSuccess === false)
            ) {
                return {
                    success: false,
                    validationErrors: data.errors || [],
                    validationWarnings: data.warnings || [],
                };
            }

            return {
                success: true,
                validationErrors: [],
                validationWarnings: [],
            };
        } catch (error: any) {
            const responseData = error?.response?.data;
            const validationErrors = Array.isArray(responseData?.errors)
                ? responseData.errors
                : [];
            const validationWarnings = Array.isArray(responseData?.warnings)
                ? responseData.warnings
                : [];
            return { success: false, validationErrors, validationWarnings };
        }
    };

    const handleFillPdfUpload = async (processId: number, file: any) => {
        try {
            const formData = new FormData();
            formData.append('ProcessId', processId.toString());
            formData.append('File', file.originFile || file);

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            await axios.post('/v1/FillPdf/UploadExcel', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                baseURL: process.env.REACT_APP_API_BASE_URL || '',
            });

            return {
                success: true,
                validationErrors: [],
                validationWarnings: [],
            };
        } catch (error: any) {
            const responseData = error?.response?.data;
            const validationErrors = Array.isArray(responseData)
                ? responseData
                : [responseData?.toString() || 'Upload failed'];

            return {
                success: false,
                validationErrors,
                validationWarnings: [],
            };
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.processName.trim()) {
            showToast(l('_error'), l('_processNameEmpty'), 'error');
            return;
        }

        const documentTypeIds = selectedDocumentTypeIds;

        const addProcessRequest: AddProcessRequest = {
            processName: formData.processName,
            processTypeId: formData.processType,
            folderId: Number(formData.location),
            documentTypeIds: documentTypeIds,
        };
        setIsUploading(true);

        addProcess(
            { data: addProcessRequest },
            {
                onSuccess: async (processResponse) => {
                    try {
                        if (
                            formData.processType ===
                            ProcessType.FillInOnlineForm
                        ) {
                            if (files.length > 0) {
                                const uploadResult =
                                    await handleOnlineFormUpload(
                                        processResponse.processId,
                                        files[0]
                                    );

                                if (uploadResult.success) {
                                    setIsUploading(false);
                                    showToast(
                                        l('_success'),
                                        l('_newProcessCreated'),
                                        'success'
                                    );
                                    navigate(
                                        `/process/${processResponse.processId}`
                                    );
                                } else {
                                    setIsUploading(false);
                                    showToast(
                                        l('_success'),
                                        l('_newProcessCreated'),
                                        'success'
                                    );
                                    navigate(
                                        `/process/${processResponse.processId}`,
                                        {
                                            state: {
                                                showUploadModal: true,
                                                validationErrors:
                                                    uploadResult.validationErrors ||
                                                    [],
                                                validationWarnings:
                                                    uploadResult.validationWarnings ||
                                                    [],
                                                originalFile:
                                                    (files[0] &&
                                                        (files[0].originFile ||
                                                            files[0])) ||
                                                    null,
                                            },
                                        }
                                    );
                                }
                            } else {
                                setIsUploading(false);
                                showToast(
                                    l('_success'),
                                    l('_newProcessCreated'),
                                    'success'
                                );
                                navigate(
                                    `/process/${processResponse.processId}`
                                );
                            }
                        } else if (
                            formData.processType === ProcessType.FillInPDF
                        ) {
                            if (files.length > 0) {
                                const uploadResult = await handleFillPdfUpload(
                                    processResponse.processId,
                                    files[0]
                                );

                                if (uploadResult.success) {
                                    setIsUploading(false);
                                    showToast(
                                        l('_success'),
                                        l('_newProcessCreated'),
                                        'success'
                                    );
                                    navigate(
                                        `/process/${processResponse.processId}`
                                    );
                                } else {
                                    setIsUploading(false);
                                    showToast(
                                        l('_success'),
                                        l('_newProcessCreated'),
                                        'success'
                                    );
                                    navigate(
                                        `/process/${processResponse.processId}`,
                                        {
                                            state: {
                                                showUploadModal: true,
                                                validationErrors:
                                                    uploadResult.validationErrors ||
                                                    [],
                                                validationWarnings:
                                                    uploadResult.validationWarnings ||
                                                    [],
                                                originalFile:
                                                    (files[0] &&
                                                        (files[0].originFile ||
                                                            files[0])) ||
                                                    null,
                                            },
                                        }
                                    );
                                }
                            } else {
                                setIsUploading(false);
                                showToast(
                                    l('_success'),
                                    l('_newProcessCreated'),
                                    'success'
                                );
                                navigate(
                                    `/process/${processResponse.processId}`
                                );
                            }
                        } else {
                            await uploadFiles(files, processResponse.processId);
                            setIsUploading(false);
                            showToast(
                                l('_success'),
                                l('_newProcessCreated'),
                                'success'
                            );
                            navigate(-1);
                        }
                    } catch (error) {
                        // Error toast is handled by the useFileUpload hook
                    }
                },
                onError: (error) => {
                    setIsUploading(false);
                    showGenericErrorToast(error);
                },
            }
        );
    };

    const handleDownloadFillPDFExample = () => {
        const documentTypeIds =
            selectedDocumentTypeIds.length > 0 ? selectedDocumentTypeIds : [];

        const requestData: DownloadExampleExportTemplateRequest = {
            documentTypeIds: documentTypeIds,
        };

        generateFillPDFTemplateMutation.mutate(
            { data: requestData },
            {
                onSuccess: (response: any) => {
                    if (response && response.uploadTemplateSasLink) {
                        const downloadLink = document.createElement('a');
                        downloadLink.href = response.uploadTemplateSasLink;
                        downloadLink.target = '_blank';
                        downloadLink.download = 'example_template.xlsx';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);

                        showToast(
                            'Success',
                            'Example template downloaded successfully',
                            'success'
                        );
                    } else {
                        showToast(
                            'Error',
                            'Failed to download example template. No URL returned.',
                            'error'
                        );
                    }
                },
                onError: (error) => {
                    console.error('Error downloading example template:', error);
                    showToast(
                        'Error',
                        'Failed to download example template. Please try again.',
                        'error'
                    );
                },
            }
        );
    };

    const handleDownloadOnlineFormExample = () => {
        const documentTypeIds =
            selectedDocumentTypeIds.length > 0 ? selectedDocumentTypeIds : [];

        const requestData: GenerateExcelTemplateRequest = {
            documentTypeIds: documentTypeIds,
        };

        generateExcelTemplateMutation.mutate(
            { data: requestData },
            {
                onSuccess: (response: GenerateExcelTemplateResponse) => {
                    if (response && response.onlineFormTemplateSASUrl) {
                        const downloadLink = document.createElement('a');
                        downloadLink.href = response.onlineFormTemplateSASUrl;
                        downloadLink.target = '_blank';
                        downloadLink.download = 'example_template.xlsx';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);

                        showToast(
                            'Success',
                            'Example template downloaded successfully',
                            'success'
                        );
                    } else {
                        showToast(
                            'Error',
                            'Failed to download example template. No URL returned.',
                            'error'
                        );
                    }
                },
                onError: (error) => {
                    console.error('Error downloading example template:', error);
                    showToast(
                        'Error',
                        'Failed to download example template. Please try again.',
                        'error'
                    );
                },
            }
        );
    };

    const panelTitle = processTypeOptions.find(
        (option) => option.value === formData.processType
    )?.label;
    const footer = (
        <div className="add-footer">
            <Button
                kind="secondary"
                icon="icon-save-outline"
                onClick={handleSubmit}
                disabled={isUploading}
                loading={isUploading}
            >
                Start
            </Button>
        </div>
    );

    const UploadComponent = useMemo(() => {
        if (
            formData.processType === ProcessType.FillInPDF ||
            formData.processType === ProcessType.FillInOnlineForm
        ) {
            return (
                <Upload
                    onChange={handleFileChange}
                    multiple={false}
                    autoUpload={false}
                    acceptFileType=".xlsx,.xls"
                    uploadInstruction="You can upload excel files (.xlsx, .xls). The max file size is 10mb."
                    uploadTitle="Upload your template file"
                    maxFileSize={10 * 1024 * 1024}
                    config={{ trigger: false, type: 'inline', size: true }}
                />
            );
        }

        return (
            <Upload
                onChange={handleFileChange}
                multiple
                autoUpload={false}
                acceptFileType=".JPG,.PNG,.PDF"
                uploadInstruction="You can upload JPG, PNG or PDF files. The max file size is 499mb."
                uploadTitle="Upload your files"
                maxFileSize={499 * 1024 * 1024}
                config={{ trigger: false, type: 'inline', size: true }}
            />
        );
    }, [handleFileChange, formData.processType]);

    return (
        <div className="create-process-container">
            <Panel title={panelTitle} footer={footer}>
                <div className="form-layout">
                    <form onSubmit={handleSubmit} className="form-section">
                        <div className="form-group">
                            <label htmlFor="processName" className="bold-label">
                                Process name{' '}
                                <span style={{ color: '#E0301E' }}>*</span>
                            </label>
                            <Input
                                id="processName"
                                type="text"
                                defaultValue={formData.processName}
                                ref={processNameRef}
                                onBlur={handleBlur}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="processType" className="bold-label">
                                Process type{' '}
                                <span style={{ color: '#E0301E' }}>*</span>
                            </label>
                            <Combobox
                                data={availableProcessTypeOptions}
                                value={formData.processType}
                                onSelect={(value) =>
                                    handleProcessTypeChange(value as number)
                                }
                                multiple={false}
                                required={false}
                                placeholder={'Select Process Type'}
                                valueKey={'value'}
                                labelKey={'label'}
                                badgeTemplate={(item) => {
                                    return (
                                        item.badgeValue && (
                                            <Badge
                                                size="lg"
                                                style={{ marginLeft: 8 }}
                                                value={item.badgeValue}
                                            ></Badge>
                                        )
                                    );
                                }}
                            />
                        </div>
                        <div className="form-group">
                            <label
                                htmlFor="documentTypes"
                                className="bold-label"
                            >
                                Document types{' '}
                                <span style={{ color: '#E0301E' }}>*</span>
                            </label>
                            <Combobox
                                data={documentTypes.map((type) => ({
                                    value: type.value,
                                    label: type.label,
                                }))}
                                showSelectAll={true}
                                required={false}
                                placeholder={'Select Document Types'}
                                multiple
                                value={selectedDocumentTypeIds}
                                onSelect={(values) =>
                                    handleDocumentTypeChange(values as number[])
                                }
                                valueKey={'value'}
                                labelKey={'label'}
                                disabled={isLoadingDocTypes}
                            />
                        </div>

                        {formData.processType === ProcessType.FillInPDF &&
                            selectedDocumentTypeIds.length > 0 && (
                                <div className="form-group">
                                    <div className="download-example-container">
                                        <Button
                                            className="download-example-button"
                                            icon="icon-download-outline"
                                            onClick={
                                                handleDownloadFillPDFExample
                                            }
                                            size="small"
                                        >
                                            Download example template
                                        </Button>
                                    </div>
                                </div>
                            )}

                        {formData.processType ===
                            ProcessType.FillInOnlineForm &&
                            selectedDocumentTypeIds.length > 0 && (
                                <div className="form-group">
                                    <div className="download-example-container">
                                        <Button
                                            className="download-example-button"
                                            icon="icon-download-outline"
                                            onClick={
                                                handleDownloadOnlineFormExample
                                            }
                                            size="small"
                                        >
                                            Download example template
                                        </Button>
                                    </div>
                                </div>
                            )}

                        <div className="form-group location-dropdown">
                            <label htmlFor="location" className="bold-label">
                                Location{' '}
                                <span style={{ color: '#E0301E' }}>*</span>
                            </label>

                            <Accordion
                                multiple={false}
                                onClick={onClickAccordion}
                                activeKeys={activeKeys}
                                style={{
                                    border: '1px solid #D1D1D1',
                                    borderRadius: '4px',
                                }}
                            >
                                <AccordionItem
                                    title={
                                        formData.folderName
                                            ? formData.folderName
                                            : folderName
                                    }
                                    itemKey="1"
                                    style={{
                                        fontWeight: 'normal',
                                        color: '#696969',
                                    }}
                                >
                                    <div className="location-section">
                                        <FolderExplorer
                                            selectedFolderId={folderId}
                                            onFolderSelect={handleFolderSelect}
                                        />
                                    </div>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </form>

                    <div className="upload-section">
                        {areRequiredFieldsFilled() && UploadComponent}
                        {areRequiredFieldsFilled() &&
                            formData.processType ===
                                ProcessType.FillInOnlineForm && (
                                <div className="validation-checkbox-container">
                                    <Checkbox
                                        checked={!validateUnnecessaryFields}
                                        onChange={(checked) =>
                                            setValidateUnnecessaryFields(
                                                !checked
                                            )
                                        }
                                    >
                                        Skip validation for hidden fields
                                    </Checkbox>
                                    <p className="checkbox-caption">
                                        When enabled, hidden field values are
                                        skipped during validation and preserved
                                        for later use if the fields become
                                        visible.
                                    </p>
                                </div>
                            )}
                    </div>
                </div>
            </Panel>
        </div>
    );
};

export default CreateProcess;
